import { storefrontApiRequest } from './shopify';

// ── Types ──

export interface ShopifyBlogArticle {
  id: string;
  title: string;
  handle: string;
  excerpt: string | null;
  contentHtml: string;
  publishedAt: string;
  tags: string[];
  image: { url: string; altText: string | null } | null;
  seo: { title: string | null; description: string | null };
  blog: { handle: string };
}

// ── Static articles (merged with Shopify blog) ──
// These articles live in the frontend and do not require a Shopify blog post.

const staticArticles: ShopifyBlogArticle[] = [];

// ── Queries ──

const BLOG_ARTICLES_QUERY = `
  query GetBlogArticles($first: Int!, $blogHandle: String!) {
    blog(handle: $blogHandle) {
      articles(first: $first, sortKey: PUBLISHED_AT, reverse: true) {
        edges {
          node {
            id
            title
            handle
            excerpt
            contentHtml
            publishedAt
            tags
            image {
              url
              altText
            }
            seo {
              title
              description
            }
            blog {
              handle
            }
          }
        }
      }
    }
  }
`;

const ARTICLE_BY_HANDLE_QUERY = `
  query GetArticleByHandle($blogHandle: String!, $articleHandle: String!) {
    blog(handle: $blogHandle) {
      articleByHandle(handle: $articleHandle) {
        id
        title
        handle
        excerpt
        contentHtml
        publishedAt
        tags
        image {
          url
          altText
        }
        seo {
          title
          description
        }
        blog {
          handle
        }
      }
    }
  }
`;

// ── Default blog handle (Shopify's default blog is "news") ──
const DEFAULT_BLOG_HANDLE = 'notizie';

// ── Fetchers ──

export async function fetchBlogArticles(
  first: number = 20,
  blogHandle: string = DEFAULT_BLOG_HANDLE,
): Promise<ShopifyBlogArticle[]> {
  const shopifyArticles: ShopifyBlogArticle[] = [];
  try {
    const data = await storefrontApiRequest(BLOG_ARTICLES_QUERY, { first, blogHandle });
    if (data?.data?.blog?.articles?.edges) {
      for (const edge of data.data.blog.articles.edges) {
        shopifyArticles.push(edge.node);
      }
    }
  } catch {
    // Shopify API unavailable (e.g., CORS in dev) — static articles still serve
  }
  // Merge static articles, sorted by publishedAt descending
  const merged = [...shopifyArticles, ...staticArticles];
  merged.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  return merged;
}

export async function fetchBlogArticleByHandle(
  articleHandle: string,
  blogHandle: string = DEFAULT_BLOG_HANDLE,
): Promise<ShopifyBlogArticle | null> {
  // Check static articles first (they override Shopify if handle collides)
  const staticMatch = staticArticles.find((a) => a.handle === articleHandle);
  if (staticMatch) return staticMatch;

  const data = await storefrontApiRequest(ARTICLE_BY_HANDLE_QUERY, {
    blogHandle,
    articleHandle,
  });
  return data?.data?.blog?.articleByHandle ?? null;
}
